import {
  AlertBox,
  Button,
  DatePicker,
  SelectInput,
  TimePicker,
} from "@life-line-lab/components-react";
import React, { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import errorHandler from "Utils/error-handler";
import { Moment } from "moment";
import moment from "moment";
import { Col, Row } from "antd";
import { CalendarOutlined, InboxOutlined } from "@ant-design/icons";
import deliveryTypeJSON from "Data/delivery-types.json";
import { EditAddDeliveryUpdateModalSchema } from "./edit-add-delivery-update-modal-validation";
import TextArea from "antd/lib/input/TextArea";
import { TFunction } from "i18next";
import { isEmpty } from "lodash";
import { FilePayload, FileTypeFilePayload } from "Types/common.types";
import { getBase64 } from "Helpers/getbase64";
import Dragger from "antd/lib/upload/Dragger";
import { toaster } from "Helpers/toaster";
import { UploadChangeParam } from "antd/lib/upload";

type InputDataType = {
  timeStamp: Moment | null;
  status: string;
  file?: FilePayload;
  unloadAt?: string;
  failedDeliveryRemarks?: string;
};

interface EditAddDeliveryUpdateModalProps {
  title: string;
  onClose: () => void;
  onSubmit: (data: InputDataType) => void;
  getDeliveryDetails: (
    deliveryType: string,
    status: string,
    data: any,
  ) => string | JSX.Element;
  initialInput?: any;
  error: any;
  selectedOrder: any;
  setError: React.Dispatch<React.SetStateAction<any>>;
  isLoading: boolean;
  handleListingSortingHub?: (input: any) => Promise<any[]>;
}

interface ExtraFieldProps {
  input: InputDataType;
  t: TFunction;
  handleFileChange: ({ fileList }: UploadChangeParam, format: string) => void;
  setInput: React.Dispatch<React.SetStateAction<InputDataType>>;
  handleListingSortingHub?: (filters: any) => Promise<any[]>;
}

const ExtraField: FC<ExtraFieldProps> = ({
  setInput,
  input,
  t,
  handleFileChange,
  handleListingSortingHub,
}) => {
  const [uploadFile, setUploadFile] = useState<any>([]);
  const [unloadServicePoint, setUnloadServicePoint] = useState<any[]>([]);

  useEffect(() => {
    const fetchServicePoints = async () => {
      if (input.status === "UNLOADED" && unloadServicePoint.length === 0) {
        const response =
          handleListingSortingHub &&
          (await handleListingSortingHub({ type: "STATION", active: true }));

        setUnloadServicePoint(
          response?.map((location) => {
            return {
              label: location.locationName,
              value: location._id,
            };
          }) || [],
        );
      }
    };

    fetchServicePoints();
  }, [input.status]);

  switch (input.status) {
    case "DELIVERED":
      return (
        <>
          <Dragger
            onRemove={() => setUploadFile([])}
            onChange={(file) => {
              setUploadFile(file.fileList);
              handleFileChange(file, ".png, .jpg, .jpeg");
            }}
            maxCount={3}
            multiple={false}
            accept=".png, .jpg, .jpeg"
            style={{ height: "150px", maxHeight: "200px" }}
            beforeUpload={() => false}
            fileList={uploadFile}
            listType="picture"
          >
            <p className="ant-upload-drag-icon">{<InboxOutlined />}</p>
            <div className="p-10">
              <p className="ant-upload-text">{t("txt.uploadImgFile")}</p>
              <p className="ant-upload-text">{t("txt.uploadFormat")}</p>
            </div>
          </Dragger>
        </>
      );
    case "DELIVERY_REFUSED":
    case "DELIVERY_NOT_POSSIBLE":
      return (
        <>
          <label className="inp-label">{t("label.remarks")}</label>
          <TextArea
            id="remarks"
            size="middle"
            autoSize={{ minRows: 2, maxRows: 6 }}
            name="remarks"
            value={input?.failedDeliveryRemarks}
            onChange={(e) =>
              setInput((prev) => ({
                ...prev,
                failedDeliveryRemarks: e.target.value,
              }))
            }
            placeholder={t("txt.remarks")}
          />
        </>
      );
    case "UNLOADED":
      return (
        <>
          <SelectInput
            showSearch={true}
            options={unloadServicePoint}
            name="status"
            placeholder={t("action.addServicePoint")}
            label={t("action.addServicePoint")}
            isRequired={true}
            value={input.unloadAt}
            onChange={(e) => {
              setInput((prev) => ({ ...prev, unloadAt: e as string }));
            }}
          />
        </>
      );
  }

  return <></>;
};

const EditAddDeliveryUpdateModal: FC<EditAddDeliveryUpdateModalProps> = (
  props,
) => {
  const {
    onClose,
    title,
    onSubmit,
    handleListingSortingHub,
    getDeliveryDetails,
    setError,
    error,
    selectedOrder,
    initialInput = {
      timeStamp: moment(),
      status: undefined,
      file: undefined,
      unloadAt: undefined,
      failedDeliveryRemarks: undefined,
    },
    isLoading,
  } = props || {};

  const { t } = useTranslation();
  const [input, setInput] = useState<InputDataType>(initialInput);

  const deliveryUpdateOptions = useMemo(() => {
    const filteredUpdate =
      deliveryTypeJSON.deliveryUpdates?.[selectedOrder.deliveryType];

    const options = Object.keys(filteredUpdate)
      .filter((key) => filteredUpdate[key] !== null)
      .map((key) => ({
        value: key,
        label: key.replace(/_/g, " "),
      }));

    return options;
  }, []);

  const handleDateChange = (date: Moment | null) => {
    if (date) {
      const newDateTime = date.clone().set({
        hour: input.timeStamp?.hour() || 0,
        minute: input.timeStamp?.minute() || 0,
      });
      setInput((prev) => ({ ...prev, timeStamp: newDateTime }));
    }
  };

  const handleTimeChange = (time: any) => {
    if (time) {
      const newDateTime = (input.timeStamp || moment()).clone().set({
        hour: time.hour(),
        minute: time.minute(),
      });
      setInput((prev) => ({ ...prev, timeStamp: newDateTime }));
    }
  };

  /**
   * function to validate uploaded file type
   * @param fileList - file data
   * @param format - accepted file format/type e.g. jpeg, pdf, etc...
   * @returns {boolean} - true or false
   */
  const handleValidateFileUploadType = (fileList, format) => {
    const regexFormat = format.match(/\w+/g);
    if (regexFormat.length > 1) {
      const allAreImages = fileList.every((file: any) =>
        ["image/png", "image/jpg", "image/jpeg"].includes(file.type),
      );

      return allAreImages;
    } else {
      return fileList[0]?.type?.includes(regexFormat);
    }
  };

  /**
   * function to handle changes in uploaded file
   * @param param0
   * @param type
   * @param format
   */
  const handleFileChange = async (
    { fileList: fileList }: UploadChangeParam,
    format: string,
  ) => {
    if (isEmpty(fileList)) return;

    const isFileTypeCompatible = handleValidateFileUploadType(fileList, format);

    if (isFileTypeCompatible) {
      const files: any[] = [];
      for (const file of fileList) {
        let base64Data: any = "";
        base64Data = await getBase64(file.originFileObj);
        files.push({
          fileName: file.name,
          fileType: FileTypeFilePayload.IMAGE,
          base64String: base64Data,
        });
      }

      setInput((prev) => ({
        ...prev,
        files: files,
      }));
    } else {
      toaster("error", "Invalid file format!");
    }
  };

  const handleSubmit = () => {
    try {
      // dont need to validate on edit because timestamp already has default value
      // and other fields are not displayed
      const value = initialInput.status
        ? true
        : EditAddDeliveryUpdateModalSchema.validateSync(input);

      if (value) onSubmit(input);
    } catch (err) {
      console.log(err);
      const { path, value }: any = errorHandler.validation(err);

      setError &&
        setError({
          ...error,
          [path]: t(`${value}`, { field: t(`label.${path}`) }),
        });
    }
  };

  const handleDeliveryUpdateDetails = () => {
    const details = getDeliveryDetails(
      selectedOrder.deliveryType,
      input.status,
      selectedOrder,
    );

    const value =
      typeof details === "string"
        ? details
        : deliveryTypeJSON.deliveryUpdates?.[selectedOrder.deliveryType]?.[
            input.status
          ];

    return (
      <TextArea
        value={value}
        readOnly={true}
        rows={2}
        style={{ resize: "none" }}
        placeholder={t("label.description")}
      />
    );
  };

  return (
    <div className="delivery-date-modal">
      <div className="ddm-header">
        <div className="ddm-title">{title}</div>
      </div>
      <Row gutter={[24, 12]}>
        <Col span={12}>
          <DatePicker
            isRequired={true}
            isError={error.timeStamp}
            suffix={<CalendarOutlined />}
            label={t("label.date")}
            value={input.timeStamp}
            onChange={handleDateChange}
            format="YYYY-MM-DD"
          />
          <AlertBox text={error.timeStamp} />
        </Col>
        <Col span={12}>
          <TimePicker
            isRequired={true}
            isError={error.timeStamp}
            placeholder={t("placeholder.selectTime")}
            label={t("label.time")}
            value={input.timeStamp}
            onChange={handleTimeChange}
            format={"hh:mm a"}
          />
          <AlertBox text={error.timeStamp} />
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <SelectInput
            isReadonly={initialInput.status}
            showSearch={true}
            options={deliveryUpdateOptions}
            name="status"
            placeholder={t("placeholder.selectStatus")}
            label={t("label.orderStatus")}
            isRequired={true}
            value={input.status}
            onChange={(e) => {
              setInput((prev) => ({ ...prev, status: e as string }));
            }}
          />
          <AlertBox text={error.status} />
        </Col>
        <Col span={24}>{handleDeliveryUpdateDetails()}</Col>
      </Row>
      {!initialInput.status && (
        <Row gutter={[24, 24]} justify="center">
          <Col span={24}>
            <ExtraField
              input={input}
              setInput={setInput}
              t={t}
              handleFileChange={handleFileChange}
              handleListingSortingHub={handleListingSortingHub}
            />
          </Col>
        </Row>
      )}
      {error?.general && <div className="u-info u-error">{error.general}</div>}
      <div className="ddm-btn-container">
        <Button
          label={t("action.cancel")}
          isDisabled={isLoading}
          onClick={() => onClose()}
        />
        <Button
          label={t("action.submit")}
          type="primary"
          isLoading={isLoading}
          onClick={() => handleSubmit()}
        />
      </div>
    </div>
  );
};

export default EditAddDeliveryUpdateModal;
